import { PhotoData, CameraSettings, PDFData } from '../types/photo';

export class PhotoService {
  private static instance: PhotoService;
  
  private constructor() {}
  
  public static getInstance(): PhotoService {
    if (!PhotoService.instance) {
      PhotoService.instance = new PhotoService();
    }
    return PhotoService.instance;
  }

  /**
   * Check if device supports camera
   */
  public isCameraSupported(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  /**
   * Get available camera devices
   */
  public async getCameraDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(device => device.kind === 'videoinput');
    } catch (error) {
      console.error('Failed to enumerate camera devices:', error);
      return [];
    }
  }

  /**
   * Get camera stream with specified settings
   */
  public async getCameraStream(settings: CameraSettings): Promise<MediaStream> {
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: settings.facingMode,
          width: { 
            ideal: settings.width,
            min: 1920, // Minimum HD resolution for OCR
            max: 3840  // Support up to 4K
          },
          height: { 
            ideal: settings.height,
            min: 1080, // Minimum HD resolution for OCR
            max: 2160  // Support up to 4K
          },
          aspectRatio: { ideal: settings.aspectRatio },
          // Additional quality constraints
          frameRate: { ideal: 30, min: 15 }
        }
      };

      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      console.error('Failed to get camera stream:', error);
      // Fallback to basic constraints if advanced features fail
      try {
        const basicConstraints: MediaStreamConstraints = {
          video: {
            facingMode: settings.facingMode,
            width: { ideal: settings.width },
            height: { ideal: settings.height },
            aspectRatio: { ideal: settings.aspectRatio }
          }
        };
        return await navigator.mediaDevices.getUserMedia(basicConstraints);
      } catch (fallbackError) {
        throw new Error('Failed to access camera. Please check permissions.');
      }
    }
  }

  /**
   * Capture photo from video stream with enhanced quality
   */
  public capturePhotoFromStream(
    stream: MediaStream, 
    quality: number = 0.95 // Increased default quality for OCR
  ): Promise<PhotoData> {
    return new Promise((resolve, reject) => {
      try {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        video.srcObject = stream;
        video.onloadedmetadata = () => {
          // Set canvas dimensions to match video (maintain full resolution)
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          // Enable high-quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw current video frame to canvas
          ctx.drawImage(video, 0, 0);

          // Convert to data URL with high quality
          const dataUrl = canvas.toDataURL('image/jpeg', quality);

          // Create photo data
          const photoData: PhotoData = {
            id: this.generatePhotoId(),
            dataUrl,
            timestamp: new Date().toISOString(),
            fileName: `photo_${Date.now()}.jpg`,
            fileSize: this.calculateFileSize(dataUrl),
            width: video.videoWidth,
            height: video.videoHeight
          };

          resolve(photoData);
        };

        video.onerror = () => {
          reject(new Error('Failed to load video stream'));
        };

        video.play();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Process photo data with enhanced quality for OCR
   */
  public async processPhoto(photoData: PhotoData, maxSize: number = 2048): Promise<PhotoData> {
    try {
      const img = new Image();
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Enable high-quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Calculate new dimensions maintaining aspect ratio
          let { width, height } = img;
          
          // Only resize if image is significantly larger than needed for OCR
          // OCR works better with higher resolution, so be conservative with resizing
          if (width > maxSize * 1.5 || height > maxSize * 1.5) {
            if (width > height) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw resized image with high quality
          ctx.drawImage(img, 0, 0, width, height);

          // Apply image enhancement for better OCR
          this.enhanceImageForOCR(ctx, width, height);

          // Convert to data URL with high quality for OCR
          const processedDataUrl = canvas.toDataURL('image/jpeg', 0.95);

          const processedPhotoData: PhotoData = {
            ...photoData,
            dataUrl: processedDataUrl,
            fileName: photoData.fileName.replace('.jpg', '_processed.jpg'),
            fileSize: this.calculateFileSize(processedDataUrl),
            width,
            height
          };

          resolve(processedPhotoData);
        };

        img.onerror = () => {
          reject(new Error('Failed to load image for processing'));
        };

        img.src = photoData.dataUrl;
      });
    } catch (error) {
      console.error('Photo processing error:', error);
      return photoData; // Return original if processing fails
    }
  }

  /**
   * Process PDF file upload
   */
  public async processPDFUpload(file: File): Promise<PDFData> {
    try {
      // Validate file type
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }

      // Validate file size (25MB limit)
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (file.size > maxSize) {
        throw new Error(`File size must be less than ${(maxSize / (1024 * 1024)).toFixed(0)}MB`);
      }

      // Convert file to base64 immediately
      const base64 = await this.convertFileToBase64(file);

      // Create PDF data with base64 content instead of File object
      const pdfData: PDFData = {
        id: this.generatePhotoId(),
        fileName: file.name,
        fileSize: file.size,
        timestamp: new Date().toISOString(),
        dataUrl: `data:application/pdf;base64,${base64}` // Store as data URL for easy access
      };

      return pdfData;
    } catch (error) {
      console.error('PDF upload processing error:', error);
      throw error;
    }
  }

  /**
   * Convert PDF data to base64 for API upload
   */
  public async convertPDFToBase64(pdfData: PDFData): Promise<string> {
    if (!pdfData.dataUrl) {
      throw new Error('PDF data URL not available');
    }
    
    // Extract base64 from data URL
    const base64 = pdfData.dataUrl.split(',')[1];
    if (!base64) {
      throw new Error('Failed to extract base64 data from PDF');
    }
    
    return base64;
  }

  /**
   * Enhance image for better OCR recognition
   */
  private enhanceImageForOCR(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    try {
      // Get image data for pixel manipulation
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      // Apply contrast enhancement for better text recognition
      const contrast = 1.2; // Increase contrast by 20%
      const brightness = 0; // No brightness adjustment

      for (let i = 0; i < data.length; i += 4) {
        // Apply contrast and brightness to RGB channels
        data[i] = Math.min(255, Math.max(0, (data[i] - 128) * contrast + 128 + brightness));     // Red
        data[i + 1] = Math.min(255, Math.max(0, (data[i + 1] - 128) * contrast + 128 + brightness)); // Green
        data[i + 2] = Math.min(255, Math.max(0, (data[i + 2] - 128) * contrast + 128 + brightness)); // Blue
        // Alpha channel remains unchanged
      }

      // Put enhanced image data back to canvas
      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      console.warn('Image enhancement failed, continuing with original:', error);
    }
  }

  /**
   * Generate unique photo ID
   */
  private generatePhotoId(): string {
    return `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Calculate file size from data URL
   */
  private calculateFileSize(dataUrl: string): number {
    const base64 = dataUrl.split(',')[1];
    if (!base64) return 0;
    
    // Approximate size calculation
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return Math.ceil((base64.length * 3) / 4) - padding;
  }

  /**
   * Convert file to base64
   */
  private async convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          const result = reader.result as string;
          // Extract base64 data (remove data:application/pdf;base64, prefix)
          const base64 = result.split(',')[1];
          if (base64) {
            resolve(base64);
          } else {
            reject(new Error('Failed to convert file to base64'));
          }
        } catch (error) {
          reject(new Error('Failed to process file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Get default camera settings optimized for OCR
   */
  public getDefaultCameraSettings(): CameraSettings {
    return {
      facingMode: 'environment', // Back camera by default
      width: 3840, // 4K resolution for best OCR quality
      height: 2160, // 4K resolution for best OCR quality
      aspectRatio: 16 / 9,
      quality: 0.95, // High quality for OCR
      focusMode: 'continuous', // Continuous focus for document scanning
      exposureMode: 'continuous', // Continuous exposure for consistent lighting
      whiteBalanceMode: 'continuous' // Continuous white balance for consistent colors
    };
  }

  /**
   * Stop camera stream
   */
  public stopCameraStream(stream: MediaStream): void {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }
}

export default PhotoService.getInstance();
